import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { ToastService } from '../services/toast.service';
import { WorkspaceFacade } from '../state/workspace/workspace.facade';
import { notifyWorkspaceSetupRequired } from './auth-entry.util';

/**
 * Requires at least one organization and one project before workspace routes
 * (the BasePage shell) can activate; otherwise sends the user into the setup
 * flow. Runs after AuthGuard, so the user is already authenticated.
 */
export const workspaceSetupGuard: CanActivateFn = async () => {
  const facade = inject(WorkspaceFacade);
  const router = inject(Router);
  const toast = inject(ToastService);

  await facade.init();

  // Fail open on load errors: redirecting a user who *has* orgs/projects into
  // the creation flow over a transient failure risks duplicate setup.
  const error = await firstValueFrom(facade.error$);
  if (error) return true;

  const organizations = await firstValueFrom(facade.organizations$);
  if (!organizations.length) {
    notifyWorkspaceSetupRequired(toast, '/create-organization');
    return router.createUrlTree(['/create-organization']);
  }

  const projects = await firstValueFrom(facade.projects$);
  if (!projects.length) {
    notifyWorkspaceSetupRequired(toast, '/create-project');
    return router.createUrlTree(['/create-project']);
  }

  return true;
};
